import React, {useEffect, useLayoutEffect, useMemo, useState} from "react";
import {CircularProgress, Paper, SvgIcon} from "@mui/material";
import {Col, Collapse} from "reactstrap";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Api from "../../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import Form from "../../../../../../../components/base/form";
import {OutlinedInput, OutlinedInputForm} from "../../../../../../../components/base/input/outlined_input";
import SaveOrRollback from "../../../../../../../components/app-specific/save-or-rollback";
import {ReactComponent as ToggleIcon} from "../../../../../../../../assets/images/warranty-statuses-reasons/toggle.svg";
import classnames from "classnames";
import IconButton from "../../../../../../../components/base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../../../../assets/images/remove.svg";
import {WarrantyAndReturnProcessSettingsApplicabilityDataGridCellsKeys} from "../../../../../../../../core/constants/table/data-grid-column-keys";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../../packages/data-grid";
import {Edit} from "@mui/icons-material";
import PartnerDataGrid from "../../../../../../../containers/data-grid";
import ProductInformationPopover from "../../../../../../../components/app-specific/draggable-popover/product-information-popover";
import useQuery from "../../../../../../../hooks/use-query";
import PartAutocomplete from "../../../../../../../components/text-fields/autocomplete/part-autocomplete";

const schema = Yup.object().shape({
    parentLimitation: Yup.number()
        .min(1, ValidateMessages.greaterThan('0')).nullable().required(ValidateMessages.required)
        .typeError(ValidateMessages.incorrectType("Number"))
})

const validate = makeValidate(schema);
const required = makeRequired(schema);

const dataGridSaveConfig = {
    name: 'PartSupportRequestProcessSettingsApplicabilityDataGrid',
    version: 1,
}

const WarrantyAndReturnProcessSettingsApplicability = ({
                                                           applicability: applicabilityProps,
                                                           selectedCategory,
                                                           loading,
                                                           isWarranty,
                                                           section,
                                                       }) => {
    const [query] = useQuery()
    const [applicabilityExceptions, setApplicabilityExceptions] = useState([]);
    const [parentApplicability, setParentApplicability] = useState({});
    const [open, setOpen] = useState(false);
    const isMounted = useIsMounted();

    /**
     * Memo version of initial value of parent component form field
     * @type {{parent}}
     */
    const initialValue = useMemo(() => {
        return {
            parentLimitation: parentApplicability?.dayLimitation ?? null
        }
    }, [parentApplicability])

    /**
     * Listens for the changes in applicabilityProp and with each change:
     * - sets the List of applicability exceptions and parent applicability in the inner state
     * - Injects "isLoading" property to the applicability objects
     */
    useEffect(() => {
        setApplicabilityExceptions(applicabilityProps
            ?.filter(e => e.part !== null)
            ?.map(e => ({
                    ...e,
                    isLoading: false,
                })
            ) ?? [])
        const parent = applicabilityProps?.find(e => e.part === null) ?? {}
        setParentApplicability({
            ...parent,
            isLoading: false,
        });
    }, [applicabilityProps])

    /**
     * Updates a part support applicability based on the provided applicability.
     * @param newDayLimitation
     * @param applicability
     */
    const updateApplicability = async (newDayLimitation, applicability) => {
        let forApi = {
            id: applicability.id,
            dayLimitation: parseInt(newDayLimitation),
            partSupportReqTypeId: parseInt(`${query.typeId}`)
        }
        if (applicability.part === null) {
            forApi = {
                ...forApi,
                categoryId: parseInt(`${query.categoryId}`),
            }
            setParentApplicability(prevState => ({...prevState, isLoading: true}))
        } else {
            forApi = {
                ...forApi,
                partNo: applicability.part.partNo,
            }
            setApplicabilityExceptions(prevState => prevState.map(e => e.id === applicability.id
                ? ({...e, isLoading: true})
                : e
            ))
        }
        const response = await Api.editPartSupportRequestApplicability(forApi)
        if (!isMounted())
            return
        if (response?.resultFlag) {
            if (applicability.part === null)
                setParentApplicability(prevState => ({...prevState, dayLimitation: forApi.dayLimitation}))
            else
                setApplicabilityExceptions(prevState => prevState.map(e => e.id === applicability.id
                    ? {...e, dayLimitation: forApi.dayLimitation}
                    : e))
        }
        if (applicability.part === null)
            setParentApplicability(prevState => ({...prevState, isLoading: false}))
        else
            setApplicabilityExceptions(prevState => prevState.map(e => e.id === applicability.id
                ? {...e, isLoading: false}
                : e))

    }

    /**
     * Creates an applicability for the provided part.
     *
     * @param  {any | null} part the part for the applicability. in case of null, the applicability is assigned to
     * the category.
     * @param {number} dayLimitation
     */
    const createApplicability = async (part, dayLimitation = parentApplicability?.dayLimitation ?? 0) => {
        let forApi = {
            dayLimitation: dayLimitation,
            partSupportReqTypeId: parseInt(`${query.typeId}`)
        }
        if (part === null) {
            setParentApplicability(prevState => ({...prevState, isLoading: true}))
            forApi = {
                ...forApi,
                categoryId: parseInt(`${query.categoryId}`),
            }
        } else {
            forApi = {
                ...forApi,
                partNo: part?.partNo ?? null
            }
        }
        const response = await Api.createPartSupportRequestApplicability(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            if (part === null)
                setParentApplicability(prevState => ({...prevState, ...response.data}))
            else
                setApplicabilityExceptions(prevState => [...prevState, {...response.data, isLoading: false}])
        }
        if (part === null)
            setParentApplicability(prevState => ({...prevState, isLoading: false}))

    }

    /**
     * Removes an applicability for a specific part from the server, then removes the inner state.
     * @param {any} applicability
     */
    const removeApplicability = async (applicability) => {
        setApplicabilityExceptions(prevState => prevState.map(e => e.id === applicability.id ? {...e, isLoading: true} : e))
        const response = await Api.removePartSupportRequestApplicability(applicability.id);
        if (!isMounted())
            return
        if (response?.resultFlag)
            setApplicabilityExceptions(prevState => prevState.filter(e => e.id !== applicability.id))
        else
            setApplicabilityExceptions(prevState => prevState.map(e => e.id === applicability.id ? ({
                ...e,
                isLoading: false
            }) : e))

    }

    /**
     * Opens delete confirmation modal to ask for users decision about removing this exceptional part.
     * If the user confirms, then removes the applicability.
     *
     * @param {any} applicability the selected part applicability
     */
    const getUserConfirmation = (applicability) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to delete this exceptional part?
                </p>
            </div>,
            {},
            (decision) => decision && removeApplicability(applicability)
        );
    }

    /**
     * Handles updating the day limitation of an applicability exceptions
     * @param prevValue
     * @param newValue
     * @param rowKey
     * @return {Promise<void>}
     */
    const onExceptionDayLimitationChanged = async (prevValue, newValue, rowKey) => {
        const _applicabilityException = applicabilityExceptions.find(e => e.id === rowKey);
        return updateApplicability(newValue, _applicabilityException);
    }

    /**
     * Memo version of data grid columns
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: WarrantyAndReturnProcessSettingsApplicabilityDataGridCellsKeys.remove,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinnedToggleable: false,
                width: 0,
            },
            {
                title: 'Part Number',
                name: WarrantyAndReturnProcessSettingsApplicabilityDataGridCellsKeys.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinnedToggleable: false,
                width: 100,
            },
            {
                title: 'Day Limitation',
                name: WarrantyAndReturnProcessSettingsApplicabilityDataGridCellsKeys.dayLimit,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinnedToggleable: false,
                width: 200,
            },
        ], [])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Memo version of  data grid rows
     * @type {unknown}
     */
    const rows = useMemo(() => {
        return applicabilityExceptions?.map((exception) => ({
            key: exception.id,
            data: exception,
            cells: {
                [WarrantyAndReturnProcessSettingsApplicabilityDataGridCellsKeys.remove]:
                    <IconButton

                        onPressed={() => getUserConfirmation(exception)}
                        disabled={exception?.isLoading}
                        color={'paper.error'}
                        className={'remove-icon'}
                    >
                        <SvgIcon component={RemoveIcon} className='icon normal'/>
                    </IconButton>,
                [WarrantyAndReturnProcessSettingsApplicabilityDataGridCellsKeys.partNo]:
                    <ProductInformationPopover partNo={exception?.part?.partNo}>
                        {exception?.part?.partNo ?? "--"}
                    </ProductInformationPopover>,
                [WarrantyAndReturnProcessSettingsApplicabilityDataGridCellsKeys.dayLimit]: {
                    value: ({openEditPopover}) => <>
                        <IconButton
                            color={'secondary'}
                            onPressed={openEditPopover}
                            disabled={exception.isLoading}
                        >
                            {
                                exception.isLoading
                                    ? <CircularProgress size={20} color={'secondary'}/>
                                    : <Edit className={"icon small"}/>
                            }
                        </IconButton>
                        <p {...dataGridCellTextProps} className={'ml-2'}>
                            {exception.dayLimitation}
                        </p>
                    </>,
                    editable: true,
                    editableValue: exception.dayLimitation,
                    EditableContentComponent: <DayLimitationEditor/>
                }
            }
        })) ?? []
    }, [applicabilityExceptions])

    return (
        <>
            <Paper
                id={section.id}
                className='paper w-100 px-3 py-2 pb-3 flex-wrap justify-content-between align-items-start'>
                <Col xs={12} className={''}>
                    <p className={'text-lg font-weight-bold mt-4 mb-4'}>
                        {section.name}
                    </p>
                </Col>
                <div className={'flex-wrap justify-content-between align-items-start applicability-card w-100'}>
                    <Col xs={12} className={'mr-4 ml-3'}>
                        <p className={'text-sm mb-1'}>
                            Category:
                            <span className={'category-title'}>
                            {selectedCategory?.title ?? ''}
                        </span>
                        </p>
                    </Col>
                    <Form
                        className={'w-100 d-flex flex-wrap mr-4 ml-3'}
                        onSubmit={() => false}
                        validate={validate}
                        initialValues={initialValue}
                        render={({values, form}) => {
                            return (
                                <>
                                    <Col xs={12} className={'d-flex flex-grow-1 align-items-center'}>
                                        <p className={'text-sm font-weight-semi-bold'}>
                                            {isWarranty && 'Warranty Expires on (days)'}
                                            {!isWarranty && 'Return Expires on (days)'}
                                        </p>
                                        <div className={'d-flex align-items-center parent-day-limitation'}>
                                            <OutlinedInputForm
                                                fullWidth={false}
                                                size={'small'}
                                                name={'parentLimitation'}
                                                placeholder={'e.g. 23'}
                                                className={'ml-3 mr-5'}
                                                type={'number'}
                                                required={required.parentLimitation}
                                                value={values.parentLimitation ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                            />
                                            <SaveOrRollback
                                                column={false}
                                                loading={parentApplicability?.isLoading}
                                                show={!!(parentApplicability?.dayLimitation !== parseInt(values.parentLimitation) && parseInt(values.parentLimitation) > 0)}
                                                onRollback={() => form.change('parentLimitation', initialValue.parentLimitation) || form.resetFieldState('parentLimitation')}
                                                onSave={() => parentApplicability?.id !== undefined
                                                    ? updateApplicability(Number(values.parentLimitation), parentApplicability)
                                                    : createApplicability(null, parseInt(values.parentLimitation))}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={12} className={'d-flex justify-content-end align-items-center'}>
                                        <p className={'text-sm'}>
                                            {applicabilityExceptions?.length ?? 0} Exceptional Parts
                                        </p>
                                        <IconButton
                                            disabled={loading}
                                            className={'ml-1 toggle-button'}
                                            color={'paper.secondary'}
                                            onPressed={() => setOpen(prevState => !prevState)}>
                                            <SvgIcon component={ToggleIcon}
                                                     className={classnames(
                                                         'icon normal toggle-icon',
                                                         {'open': open}
                                                     )}
                                            />
                                        </IconButton>
                                    </Col>
                                    <Col xs={12} className={'mb-3'}>
                                        <Collapse isOpen={open}>
                                            <div className={'d-flex flex-column'}>
                                                <Col xs={12} className={'d-flex align-items-center p-0 mb-3'}>
                                                <span className={'ex-parts'}>
                                                    Exceptional Parts
                                                </span>
                                                    <div className={'dotted-divider'}/>
                                                </Col>
                                                <div className={'inner-container'}>
                                                    <p className={'label'}>
                                                        Add a new exceptional part
                                                    </p>
                                                    <PartAutocomplete
                                                        clearOnBlur
                                                        disabled={!!values.partNo?.length}
                                                        label={"Add Product"}
                                                        value={null}
                                                        onChange={(_, value) => value && createApplicability(value)}
                                                        filterOptions={(options) => options.filter(e => !applicabilityExceptions?.some(exception => exception.part?.partNo === e.partNo))}
                                                    />
                                                    <PartnerDataGrid
                                                        {...dataGridSaveConfig}
                                                        initialLayoutState={{
                                                            columns: columns,
                                                        }}
                                                        columns={dataGridColumns}
                                                        rows={rows}
                                                        hideFooter
                                                        hideToolbar
                                                        visibleRows={6}
                                                        state={{
                                                            loading: {state: loading},
                                                        }}
                                                        classNames={{
                                                            container: "mt-3"
                                                        }}
                                                        onCellEdited={onExceptionDayLimitationChanged}
                                                    />
                                                </div>
                                            </div>
                                        </Collapse>
                                    </Col>
                                </>
                            )
                        }}
                    />
                </div>
            </Paper>
        </>
    );
}

const DayLimitationEditor = ({value, setValue, setCanSubmitChanges}) => {

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value can be submitted
     *   which is possible when the input value is valid number and greater than minimum price
     */
    useLayoutEffect(() => {
        const parsed = Number(value)
        setCanSubmitChanges(!Number.isNaN(parsed));
    }, [value])

    /**
     * Changes the value of this editor.
     *
     * - Checks if entered value is greater than minimum price
     * @param value
     */
    const onValueChanged = (value) => {
        const parsed = Number(value)
        if (!value || Number.isNaN(parsed))
            return setValue(undefined);
        setValue(Math.max(0, parsed))
    }

    return (
        <>
            <OutlinedInput
                fullWidth
                size={'small'}
                label={"New Day Limitation"}
                value={value?.toString() ?? ''}
                onChange={onValueChanged}
                type={'number'}
                inputProps={{
                    min: 0,
                }}
            />
        </>
    );
}

export default WarrantyAndReturnProcessSettingsApplicability;
